import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { ShieldCheck, Mail, ArrowLeft, Zap } from 'lucide-react';
import { toast } from 'sonner';
let autoLoginCodeSent = null;
const NeonCard = ({ children }) => (
  <div style={{ position:'relative', borderRadius:'16px' }}>
    <div style={{ position:'absolute', inset:'-1px', background:'linear-gradient(135deg,#00f5ff 0%,#a855f7 50%,#ff00ff 75%,#00f5ff 100%)', backgroundSize:'300% 300%', animation:'borderRotate 4s linear infinite', borderRadius:'16px', zIndex:0 }}/>
    <div style={{ position:'relative', zIndex:1, background:'rgba(5,6,18,0.88)', borderRadius:'15px', backdropFilter:'blur(28px)', WebkitBackdropFilter:'blur(28px)' }}>
      {children}
    </div>
  </div>
);

const Landing = ({ prefilledCode = '', onOtpHandled }) => {
  const navigate    = useNavigate();
  const { setAuth } = useAuth();
  const [step, setStep]             = useState('code');
  const [accessCode, setAccessCode] = useState('');
  const [otp, setOtp]               = useState(['','','','','','']);
  const [loading, setLoading]       = useState(false);
  const [emailHint, setEmailHint]   = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [focused, setFocused]       = useState(null);
  const otpRefs = useRef([]);
  useEffect(() => {
  if (!prefilledCode) return;
  const code = prefilledCode.trim();
  setAccessCode(code);

  // Ya se lanzó el auto-login para este código: no repetir
  if (autoLoginCodeSent === code) return;
  autoLoginCodeSent = code;

  // Quita ?code= de la URL: un F5 ya no reenvía otro OTP
  window.history.replaceState({}, '', window.location.pathname);

  const autoLogin = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Código inválido');
      if (data.access_token) {
        setAuth(data.access_token, data.user);
        redirectByRole(data.user.role);
        return;
      }
      if (data.requires_otp) {
        setEmailHint(data.email_hint);
        setStep('otp');
        setResendCooldown(60);
        toast.success(data.message);
      }
    } catch (err) {
      autoLoginCodeSent = null;   // permite reintentar si falló
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  autoLogin();
}, [prefilledCode]);
  const redirectByRole = (role) => {

    if (role==='superadmin') navigate('/superadmin');
    else if (role==='admin') navigate('/admin');
    else navigate('/dashboard');
  };

  const handleLoginByCode = async (e) => {
  e.preventDefault(); setLoading(true);
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({access_code:accessCode.trim()})
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail||'Código inválido');
    if (data.access_token) {
      setAuth(data.access_token,data.user);
      redirectByRole(data.user.role);
      return;
    }
    if (data.requires_otp) {
      setEmailHint(data.email_hint);
      setStep('otp');
      setResendCooldown(60);
      toast.success(data.message);
    }
  } catch(err) { toast.error(err.message); } finally { setLoading(false); }
};



  const handleVerifyOtp = async (otpOverride) => {
    const otpStr = otpOverride??otp.join('');
    if (otpStr.length<6) return; setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify-otp`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({access_code:accessCode.trim(),otp:otpStr})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail||'Código incorrecto');
      setAuth(data.access_token,data.user); redirectByRole(data.user.role);
    } catch(err) { toast.error(err.message); setOtp(['','','','','','']); otpRefs.current[0]?.focus(); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendCooldown>0) return; setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({access_code:accessCode.trim()})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Nuevo código enviado'); setResendCooldown(60); setOtp(['','','','','','']); otpRefs.current[0]?.focus();
    } catch(err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next=[...otp]; next[idx]=val.slice(-1); setOtp(next);
    if (val&&idx<5) otpRefs.current[idx+1]?.focus();
    if (next.join('').length===6) setTimeout(()=>handleVerifyOtp(next.join('')),80);
  };
  const handleOtpKeyDown = (idx,e) => { if (e.key==='Backspace'&&!otp[idx]&&idx>0) otpRefs.current[idx-1]?.focus(); };
  const handleOtpPaste = (e) => {
    const p=e.clipboardData.getData('text').replaceAll(/\D/g,'').slice(0,6);
    if (p.length===6) { setOtp(p.split('')); otpRefs.current[5]?.focus(); setTimeout(()=>handleVerifyOtp(p),80); }
  };

  const inp = (id) => ({
    width:'100%', padding:'13px 16px', boxSizing:'border-box',
    background: focused===id ? 'rgba(0,245,255,0.07)' : 'rgba(0,0,0,0.5)',
    border: focused===id ? '1px solid #00f5ff' : '1px solid rgba(0,245,255,0.16)',
    borderRadius:'10px', color:'#dff9ff', fontSize:'14px',
    fontFamily:"'Share Tech Mono',monospace", outline:'none', transition:'all 0.25s',
    boxShadow: focused===id ? '0 0 20px rgba(0,245,255,0.16)' : 'none',
  });
  const lbl = { display:'block', fontSize:'9px', fontWeight:700, letterSpacing:'0.22em', color:'#00d4e6', marginBottom:'7px', fontFamily:"'Share Tech Mono',monospace" };
  const btnS = (disabled) => ({
    width:'100%', padding:'14px', border:'none', borderRadius:'10px',
    background: disabled ? 'rgba(0,245,255,0.07)' : 'linear-gradient(135deg,#00f5ff,#a855f7)',
    color: disabled ? 'rgba(0,245,255,0.28)' : '#000', fontSize:'12px', fontWeight:700,
    letterSpacing:'0.18em', cursor: disabled?'not-allowed':'pointer', transition:'all 0.3s',
    boxShadow: disabled ? 'none' : '0 0 32px rgba(0,245,255,0.3)',
    fontFamily:"'Share Tech Mono',monospace",
  });

  return (
    <div style={{
      minHeight:'100vh',
      display:'flex', alignItems:'center', justifyContent:'center',
      position:'relative', overflow:'hidden',
      fontFamily:"'Share Tech Mono',monospace",
      backgroundImage:"url('/bg-login.png')",
      backgroundSize:'cover',
      backgroundPosition:'center center',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');
        @keyframes borderRotate { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes floatIcon    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes spinSlow     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes scanLine     { 0%{top:-2px} 100%{top:100%} }
        @keyframes flicker      { 0%,19%,21%,23%,25%,54%,56%,100%{opacity:1} 20%,22%,24%,55%{opacity:0.4} }
        input::placeholder { color:rgba(0,245,255,0.18); }
        .login-btn:hover:not(:disabled) { box-shadow:0 0 50px rgba(0,245,255,0.5) !important; transform:translateY(-2px); }
      `}</style>

      <div style={{ position:'fixed', inset:0, zIndex:0, background:'rgba(3,3,16,0.58)', backdropFilter:'blur(0.5px)' }}/>
      <div style={{ position:'fixed', left:0, right:0, height:'2px', zIndex:1, background:'linear-gradient(90deg,transparent,rgba(0,245,255,0.35),transparent)', animation:'scanLine 10s linear infinite' }}/>
      <div style={{ position:'fixed', top:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg,transparent,rgba(0,245,255,0.6),rgba(168,85,247,0.4),transparent)', zIndex:2 }}/>
      <div style={{ position:'fixed', bottom:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg,transparent,rgba(168,85,247,0.5),rgba(0,245,255,0.6),transparent)', zIndex:2 }}/>
      <div style={{ position:'fixed', top:0, bottom:0, left:0, width:'2px', background:'linear-gradient(180deg,rgba(0,245,255,0.4),rgba(168,85,247,0.25),transparent)', zIndex:2 }}/>
      <div style={{ position:'fixed', top:0, bottom:0, right:0, width:'2px', background:'linear-gradient(180deg,rgba(168,85,247,0.4),rgba(0,245,255,0.25),transparent)', zIndex:2 }}/>

      <div style={{ position:'fixed', top:'24px', left:'28px', display:'flex', alignItems:'center', gap:'10px', zIndex:10 }}>
        <div style={{ width:'36px', height:'36px', background:'linear-gradient(135deg,#00f5ff,#a855f7)', borderRadius:'9px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 24px rgba(0,245,255,0.4)' }}>
          <span style={{ fontSize:'13px', fontWeight:700, color:'#000' }}>NT</span>
        </div>
        <div>
          <div style={{ fontSize:'11px', fontWeight:700, color:'#00f5ff', letterSpacing:'0.28em', animation:'flicker 9s infinite', textShadow:'0 0 10px rgba(0,245,255,0.6)' }}>NEXTECH</div>
          <div style={{ fontSize:'8px', color:'rgba(0,245,255,0.38)', letterSpacing:'0.35em' }}>ACADEMY</div>
        </div>
      </div>

      <div style={{ width:'100%', maxWidth:'400px', padding:'20px', position:'relative', zIndex:10 }}>
        <NeonCard>
          <div style={{ padding:'36px 32px' }}>

            {step==='code' && (
              <>
                <div style={{ textAlign:'center', marginBottom:'30px' }}>
                  <div style={{ width:'62px', height:'62px', margin:'0 auto 18px', background:'rgba(0,245,255,0.05)', border:'1px solid rgba(0,245,255,0.28)', borderRadius:'14px', display:'flex', alignItems:'center', justifyContent:'center', animation:'floatIcon 3.5s ease-in-out infinite', boxShadow:'0 0 30px rgba(0,245,255,0.18)' }}>
                    <Zap size={28} style={{ color:'#00f5ff', filter:'drop-shadow(0 0 8px rgba(0,245,255,0.8))' }} />
                  </div>
                  <h1 style={{ fontSize:'24px', fontWeight:700, color:'#e0f7ff', margin:'0 0 6px', letterSpacing:'0.14em', textShadow:'0 0 24px rgba(0,245,255,0.25)' }}>ACCEDER</h1>
                  <p style={{ fontSize:'9px', color:'rgba(0,245,255,0.38)', letterSpacing:'0.22em', margin:0 }}>
                    INGRESA TU CÓDIGO DE ACCESO
                  </p>
                </div>

                <form onSubmit={handleLoginByCode} style={{ display:'flex', flexDirection:'column', gap:'18px' }}>
                  <div>
                    <label style={lbl}>Código de Acceso</label>
                    <input type="text" value={accessCode} onChange={e=>setAccessCode(e.target.value.toUpperCase().replaceAll(/[^A-Z0-9-]/g,''))}
                      style={{...inp('code'),textAlign:'center',fontSize:'22px',letterSpacing:'0.45em'}}
                      onFocus={()=>setFocused('code')} onBlur={()=>setFocused(null)}
                      placeholder="XXXX-XXXX" maxLength={9} autoComplete="off" autoFocus required />
                  </div>
                  <button type="submit" className="login-btn" style={btnS(loading||accessCode.length<4)} disabled={loading||accessCode.length<4}>
                    {loading?'⟳  VERIFICANDO...':'▶  ENTRAR AL SISTEMA'}
                  </button>
                </form>

                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'22px' }}>
                  <div style={{ flex:1, height:'1px', background:'rgba(0,245,255,0.07)' }}/>
                  <span style={{ fontSize:'7px', color:'rgba(0,245,255,0.22)', letterSpacing:'0.25em' }}>NEXTECH v2.0</span>
                  <div style={{ flex:1, height:'1px', background:'rgba(0,245,255,0.07)' }}/>
                </div>
              </>
            )}

            {step==='otp' && (
              <>
                <div style={{ textAlign:'center', marginBottom:'22px' }}>
                  <div style={{ width:'62px', height:'62px', margin:'0 auto 18px', border:'2px solid rgba(0,245,255,0.32)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,245,255,0.04)', animation:'spinSlow 9s linear infinite', boxShadow:'0 0 28px rgba(0,245,255,0.18)' }}>
                    <ShieldCheck size={27} style={{ color:'#00f5ff', filter:'drop-shadow(0 0 8px rgba(0,245,255,0.8))' }} />
                  </div>
                  <h1 style={{ fontSize:'20px', fontWeight:700, color:'#e0f7ff', margin:'0 0 6px', letterSpacing:'0.14em' }}>VERIFICACIÓN</h1>
                  <p style={{ fontSize:'9px', color:'rgba(0,245,255,0.38)', margin:0 }}>
                    Código enviado a <strong style={{ color:'#00f5ff', textShadow:'0 0 8px rgba(0,245,255,0.5)' }}>{emailHint}</strong>
                  </p>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:'rgba(0,245,255,0.04)', border:'1px solid rgba(0,245,255,0.14)', borderRadius:'10px', marginBottom:'20px' }}>
                  <Mail size={12} style={{ color:'#00f5ff', flexShrink:0 }} />
                  <p style={{ fontSize:'9px', color:'rgba(0,245,255,0.45)', margin:0 }}>Revisa tu bandeja. Caduca en <strong style={{ color:'#00f5ff' }}>10 min</strong>.</p>
                </div>
                <div style={{ display:'flex', gap:'7px', justifyContent:'center', marginBottom:'20px' }} onPaste={handleOtpPaste}>
                  {otp.map((digit,idx) => (
                    <input key={idx} ref={el=>otpRefs.current[idx]=el} type="text" inputMode="numeric" maxLength={1} value={digit}
                      onChange={e=>handleOtpChange(idx,e.target.value)} onKeyDown={e=>handleOtpKeyDown(idx,e)}
                      style={{ width:'44px', height:'52px', textAlign:'center', fontSize:'20px', fontWeight:700, background:digit?'rgba(0,245,255,0.09)':'rgba(0,0,0,0.55)', border:digit?'2px solid #00f5ff':'1px solid rgba(0,245,255,0.14)', borderRadius:'10px', color:digit?'#00f5ff':'rgba(0,245,255,0.18)', outline:'none', boxShadow:digit?'0 0 18px rgba(0,245,255,0.28)':'none', transition:'all 0.2s', fontFamily:"'Share Tech Mono',monospace", opacity:loading?0.5:1 }}
                      autoFocus={idx===0}
                    />
                  ))}
                </div>
                <button className="login-btn" style={btnS(loading||otp.join('').length<6)} onClick={()=>handleVerifyOtp()} disabled={loading||otp.join('').length<6}>
                  {loading?'⟳  VERIFICANDO...':'▶  CONFIRMAR ACCESO'}
                </button>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'16px' }}>
                  <button onClick={()=>{setStep('code');setOtp(['','','','','','']);}} style={{ display:'flex', alignItems:'center', gap:'5px', background:'none', border:'none', cursor:'pointer', color:'rgba(0,245,255,0.32)', fontSize:'9px', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'0.14em' }}>
                    <ArrowLeft size={11}/> VOLVER
                  </button>
                  <button onClick={handleResend} disabled={resendCooldown>0||loading} style={{ background:'none', border:'none', cursor:resendCooldown>0?'not-allowed':'pointer', color:resendCooldown>0?'rgba(0,245,255,0.22)':'#00f5ff', fontSize:'9px', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'0.14em', fontWeight:700 }}>
                    {resendCooldown>0?`REENVIAR (${resendCooldown}s)`:'REENVIAR CÓDIGO'}
                  </button>
                </div>
              </>
            )}

          </div>
        </NeonCard>
      </div>
    </div>
  );
};

export default Landing;